import {
  AbstractBuildstep,
  type OptionDB,
  ThreeToolsInstance,
} from "@project/emerge";
import type {
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
} from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import without from "lodash/without";
import * as THREE from "three";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { LTPartType } from "../enum/PartType";
import type { CornerDefinition } from "../interfaces/CornerProperties";
import type { CornerType } from "../interfaces/CornerType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { RollerType } from "../interfaces/RollerBoardProperties";
import { DefaultGroupName, getOrientationOnSide } from "../structure/Rect";
import { RectPropertiesBySide } from "../structure/RectProperties";
import {
  type CaseCornerPosition,
  getCornerDragHandler,
} from "./corner-drag-handlers";

// --- Dependency Data Interface ---
interface CornerDependencyData {
  partId: string;
  groupName: string;
  buildstepName: string;
  partKind: "Corner";
  type: LTPartType.Corner | LTPartType.Roller;
  partSelection: CornerType | RollerType;
  position: THREE.Vector3;
  side: Sides;
  mirror: boolean;
  useLVariant: boolean;
  orientation: number;
  finalRotation: THREE.Euler;
  caseCornerPosition?: CaseCornerPosition;
  dragConstraint?: DragConstraint;
  settingsContext?: Record<string, unknown>;
}

/**
 * CornerBuildStep handles corner parts for flight cases.
 * Supports drag-and-drop for resizing interior dimensions.
 */
export class CornerBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  CornerDefinition,
  CornerDependencyData
> {
  constructor(private readonly optionDb: OptionDB) {
    super("CornerBuildStep");
  }

  private isCornerType(
    partSelection: CornerType | RollerType,
  ): partSelection is CornerType {
    return "angleVariant" in partSelection;
  }

  private getMatchingCut(group: string, settings: FlightCaseSettings) {
    return group === DefaultGroupName
      ? settings.cuts?.[0]
      : settings.cuts?.find((cut) => cut.groupName === group);
  }

  private computeReferenceSide(
    group: string,
    matchingCut: NonNullable<FlightCaseSettings["cuts"]>[number],
  ): Sides {
    return (group === DefaultGroupName) !== (matchingCut.invertCover === true)
      ? ((matchingCut.reference ^ 1) as Sides)
      : matchingCut.reference;
  }

  private enforceOrientationIfNeeded(
    reference: Sides,
    partSelection: CornerType | RollerType,
  ): Sides {
    return partSelection.forceOrientation ? Sides.BOTTOM : reference;
  }

  private computeBracketOffset(
    sides: Sides[],
    settings: FlightCaseSettings,
  ): THREE.Vector3 {
    const offset = new THREE.Vector3(0, 0, 0);
    if (sides?.length) {
      const strength = settings.exteriorOptions.angleSections?.strength ?? 0;
      for (const side of sides) {
        const props = RectPropertiesBySide[side];
        if (!props) {
          console.warn(
            `CornerBuildStep: Could not find RectProperties for side ${side}`,
          );
          continue;
        }
        offset.add(props.normalDir.clone().multiplyScalar(strength));
      }
    }
    return offset;
  }

  private resolvePartSelectionWithAngleVariant(
    selection: CornerType | RollerType,
    useLVariant: boolean,
  ): CornerType | RollerType {
    if (!this.isCornerType(selection) || !selection.angleVariant)
      return selection;
    if (!useLVariant) return selection;

    const angleVariantMesh = selection.meshes?.angleVariant;
    if (!angleVariantMesh) return selection;

    // Use angleVariant.code if available, otherwise use the base name (selection.name)
    // Don't use angleVariantMesh.name as it might contain the "-angleVariant" suffix from old data
    const name = selection.angleVariant.code || selection.name;

    // Append " (L-Ecke)" to description when using L-variant
    const description = selection.description
      ? `${selection.description} (L-Ecke)`
      : "(L-Ecke)";

    return {
      ...selection,
      name,
      description,
      meshes: {
        default: angleVariantMesh,
      },
    } as CornerType;
  }

  private computeOrientation(
    reference: Sides,
    remainingSides: Sides[],
  ): number {
    return getOrientationOnSide(reference, remainingSides);
  }

  private computeFinalRotation(
    reference: Sides,
    orientation: number,
    mirror: boolean,
  ): THREE.Euler {
    if (!mirror) return this.rotateOntoSide(reference, orientation);
    const mirrorRotation = new THREE.Euler(
      ThreeToolsInstance.DEG2RAD(-90),
      ThreeToolsInstance.DEG2RAD(90),
      0,
      "YXZ",
    );
    const sideRotation = this.rotateOntoSide(reference, orientation);
    const mirrorQuaternion = new THREE.Quaternion().setFromEuler(
      mirrorRotation,
    );
    const sideQuaternion = new THREE.Quaternion().setFromEuler(sideRotation);
    const finalQuaternion = sideQuaternion.multiply(mirrorQuaternion);
    return new THREE.Euler().setFromQuaternion(finalQuaternion);
  }

  private toPartType(
    selection: CornerType | RollerType,
  ): LTPartType.Corner | LTPartType.Roller {
    return selection.kind === LTCaseUserOption.RollerType
      ? LTPartType.Roller
      : LTPartType.Corner;
  }

  private buildDependency(args: {
    partId: string;
    groupName: string;
    finalSelection: CornerType | RollerType;
    finalPosition: THREE.Vector3;
    side: Sides;
    mirror: boolean;
    useLVariant: boolean;
    orientation: number;
    finalRotation: THREE.Euler;
  }): CornerDependencyData {
    return {
      partId: args.partId,
      partKind: "Corner",
      type: this.toPartType(args.finalSelection),
      groupName: args.groupName,
      buildstepName: this.buildstepName,
      partSelection: args.finalSelection,
      position: args.finalPosition,
      side: args.side,
      mirror: args.mirror,
      useLVariant: args.useLVariant,
      orientation: args.orientation,
      finalRotation: args.finalRotation,
    };
  }

  private processCorner(
    corner: FlightCaseBlueprint["corners"][number],
    settings: FlightCaseSettings,
    partId: string,
  ) {
    if (!corner.partSelection) {
      console.error(
        `CornerBuildStep: No partSelection found for corner id: ${partId}. Skipping.`,
      );
      return null;
    }
    const matchingCut = this.getMatchingCut(corner.group, settings);
    if (!matchingCut) {
      console.error(
        `CornerBuildStep: No matching cut found for corner group: ${corner.group}. Skipping.`,
      );
      return null;
    }
    let reference = this.computeReferenceSide(corner.group, matchingCut);
    reference = this.enforceOrientationIfNeeded(
      reference,
      corner.partSelection,
    );
    const bracketOffset = this.computeBracketOffset(corner.sides, settings);
    const finalPosition = corner.position.clone().add(bracketOffset);
    const finalSelection = this.resolvePartSelectionWithAngleVariant(
      corner.partSelection,
      corner.useLVariant,
    );
    const orientation = this.computeOrientation(
      reference,
      without(corner.sides, reference),
    );
    const finalRotation = this.computeFinalRotation(
      reference,
      orientation,
      corner.mirror,
    );
    return this.buildDependency({
      partId,
      groupName: corner.group,
      finalSelection,
      finalPosition,
      side: reference,
      mirror: corner.mirror,
      useLVariant: corner.useLVariant,
      orientation,
      finalRotation,
    });
  }

  /**
   * Determine corner position relative to case center.
   */
  private determineCaseCornerPosition(
    position: THREE.Vector3,
  ): CaseCornerPosition {
    const xSide = position.x < 0 ? "left" : "right";
    const zSide = position.z < 0 ? "Front" : "Back";
    const yLevel = position.y < 0 ? "Bottom" : "Top";
    return `${xSide}${zSide}${yLevel}` as CaseCornerPosition;
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, CornerDependencyData> {
    const dependencies = new Map<string, CornerDependencyData>();
    let cornerCounter = 0;

    // Get the appropriate drag handler for this interior type
    const dragHandler = getCornerDragHandler(
      this.optionDb,
      settings,
      this.buildstepName,
    );

    for (const corner of blueprint.corners) {
      const partId = `corner_${cornerCounter++}`;
      const dep = this.processCorner(corner, settings, partId);
      if (!dep) continue;

      // Add drag constraint if handler is available
      if (dragHandler) {
        const caseCornerPosition = this.determineCaseCornerPosition(
          dep.position,
        );
        const dragData = dragHandler.buildConstraint(
          caseCornerPosition,
          dep.position,
          blueprint.innerDimensions,
          settings,
        );
        if (dragData) {
          dep.caseCornerPosition = caseCornerPosition;
          dep.dragConstraint = dragData.dragConstraint;
          dep.settingsContext = dragData.settingsContext;
        }
      }

      dependencies.set(partId, dep);
    }

    return dependencies;
  }

  public createPart(
    partId: string,
    dependencies: CornerDependencyData,
  ): CornerDefinition {
    return {
      id: partId,
      type: dependencies.type,
      group: dependencies.groupName,
      buildstepName: dependencies.buildstepName,
      isSupplyPart: true,
      orientation: dependencies.orientation,
      partSelection: dependencies.partSelection,
      position: dependencies.position.clone(),
      rotation: dependencies.finalRotation,
      ...(dependencies.dragConstraint
        ? { dragConstraint: dependencies.dragConstraint }
        : {}),
      ...(dependencies.settingsContext
        ? { settingsContext: dependencies.settingsContext }
        : {}),
    };
  }

  /**
   * Handle drag updates by delegating to the appropriate handler.
   */
  public updateSettingsFromDrag(
    _partId: string,
    newPosition: THREE.Vector3,
    _constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    if (!settingsContext) return null;

    const dragHandler = getCornerDragHandler(
      this.optionDb,
      currentSettings,
      this.buildstepName,
    );
    if (!dragHandler) return null;

    return dragHandler.updateSettings(
      newPosition,
      settingsContext,
      currentSettings,
      options,
    );
  }
}
